import { isDefined } from "@clipboard-health/util-ts";
import { isCapacitorPlatform } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { USER_EVENTS } from "@src/constants";
import { Deploy } from "cordova-plugin-ionic";
import { CheckForUpdateResponse } from "cordova-plugin-ionic/dist/IonicCordova";
import { useCallback, useEffect, useState } from "react";

import { configureDeploy } from "./utils";

enum OtaState {
  IDLE = "idle",
  CHECKING = "checking",
  DOWNLOADING = "downloading",
  AVAILABLE = "available",
  NOT_AVAILABLE = "not-available",
}

export function useOtaUpdate(workerId?: string) {
  const [otaUpdateInfo, setOtaUpdateInfo] = useState<CheckForUpdateResponse>();
  const [otaUpdateProgress, setOtaUpdateProgress] = useState(0);
  const [otaState, setOtaState] = useState<OtaState>(OtaState.IDLE);

  const checkForOtaUpdate = useCallback(async () => {
    setOtaState(OtaState.CHECKING);

    await configureDeploy();
    const updateInfo = await Deploy.checkForUpdate();

    if (updateInfo?.available) {
      setOtaState(OtaState.DOWNLOADING);
      logEvent(USER_EVENTS.OTA_UPDATE_DOWNLOAD_STARTED, {
        buildId: updateInfo.build,
      });

      const startTime = new Date();
      await Deploy.downloadUpdate((progress) => {
        if (progress && progress % 5 === 0) {
          setOtaUpdateProgress(progress);
        }
      });

      const extractionStartTime = new Date();
      await Deploy.extractUpdate();
      const endTime = new Date();
      const downloadOnlyTimeInSeconds =
        (extractionStartTime.getTime() - startTime.getTime()) / 1000;
      const extractionTimeInSeconds = (endTime.getTime() - extractionStartTime.getTime()) / 1000;
      const totalTimeInSeconds = (endTime.getTime() - startTime.getTime()) / 1000;
      logEvent(USER_EVENTS.OTA_UPDATE_DOWNLOADED, {
        buildId: updateInfo.build,
        // @deprecated. Used for metrics right now, but should move to `totalTimeTakenInSeconds` for newer metrics
        downloadTimeSeconds: totalTimeInSeconds,
        downloadOnlyTimeInSeconds,
        extractionTimeInSeconds,
        totalTimeInSeconds,
      });
      setOtaUpdateInfo(updateInfo);
      setOtaState(OtaState.AVAILABLE);
    } else {
      setOtaState(OtaState.NOT_AVAILABLE);
    }
  }, []);

  useEffect(() => {
    if (isDefined(workerId) && otaState === OtaState.IDLE && isCapacitorPlatform()) {
      // downloaded build will be auto applied when worker reopens the app
      try {
        checkForOtaUpdate();
        // FIXME: Automatically disabled during ESLint config update
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (error) {
        logEvent(USER_EVENTS.OTA_UPDATE_DOWNLOAD_ERROR);
      }
    }
  }, [workerId, checkForOtaUpdate, otaState]);

  return {
    otaUpdateInfo,
    otaUpdateProgress,
    otaState,
  };
}
